import type { MixedItemTag } from "../tag";
import type { AIFeedbackResponse } from "./ai";
import type { BaseItemContent } from "./content";
import type { ItemFeedback } from "./feedback";
import type { Grading } from "./grading";
import type { ItemMetadata, StimulusMetadata } from "./metadata";
import type { ItemResponse } from "./response";
import type { OriginalItemTypes } from "./types";

export type SubmittedRawInput = {
	inputType: "audio" | "text";
	value: string;
};

export type UserSubmittedAnswer = {
	rawInput?: SubmittedRawInput | null;
};

export type AnsweredFormState = {
	response?: string;
	isCorrect?: boolean;
};

export type Answer = {
	selectedChoiceIds?: string[];
	xp?: number;
	score?: number;
	points?: number;
	/** Raw-scale points echoed by the backend; `points` can be normalized for AI items. */
	rawPoints?: number;
	/** Backend display-policy overall (sum of per-criterion rounded scores); authority for review. */
	displayPoints?: number;
	timeTaken?: number;
	predicted_mastery?: number;
	timeElapsed?: number;
	confidence?: number;
	correct?: boolean;
	aiResponse?: AIFeedbackResponse;
	marked?: boolean;
	highlight?: string;
	/** The raw response the user submitted (AI items). Legacy payloads sent a boolean flag. */
	userSubmittedAnswer?: UserSubmittedAnswer | boolean | null;
	/** Blank/dropdown drag responses (FIBDDR/FIBDPR): blankId -> wordId | choiceId. */
	selectedBlankValues?: Record<string, string>;
	/** Reorder responses (ROPR): segment ids in the order the user arranged them. */
	selectedOrder?: string[];
	/** Typed free-text responses (WFDL/writing): the raw text the user submitted. */
	userResponseText?: string;
	/** Per-field form responses (note completion): fieldId -> graded response. */
	answeredFormStates?: Record<string, AnsweredFormState>;
};

export interface BaseItemSchema {
	id?: number;
	versionId?: number;
	recordType: "SCENARIO" | "ITEM";
	type: OriginalItemTypes;
	content: BaseItemContent;
	feedback?: ItemFeedback;
	tags?: MixedItemTag[];
	response: ItemResponse;
	metadata: ItemMetadata;
	stimulus?: BaseItemContent;
	stimulusMetadata?: StimulusMetadata;
	parentRecordType?: "SCENARIO" | "ITEM";
	scoring?: Grading;
	answer?: Answer;
	currentStimulusItemIndex?: number;
	numTotalStimulusItems?: number;
	lastUpdatedAt?: string;
	completedAt?: string;
	lastUpdated?: boolean;
}
